import React, { useState, useEffect } from 'react'
import { 
  Card, 
  Typography, 
  Button, 
  Spin, 
  Alert, 
  Descriptions, 
  Statistic, 
  Space,
  Result,
  message,
  Radio
} from 'antd'
import { 
  ArrowLeftOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  BankOutlined
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
// No longer need Stripe Elements for payment links
import { customerApi } from '../services/customerApi'
import { useCustomerAuth } from '../contexts/CustomerAuthContext'
import { HelcimPayCheckout } from '../components/payments/HelcimPayCheckout'
import { FinixPayCheckout } from '../components/payments/FinixPayCheckout'
import { BanccardPayCheckout } from '../components/payments/BanccardPayCheckout'
import { AffirmFinancingBlock } from '../components/financing/AffirmFinancingBlock'
import { env } from '../config/environment'
import { amountDollarsToCents } from '@serviceflow/shared'
import dayjs from 'dayjs'

const { Title, Text } = Typography

interface PaymentMethodSelectorProps {
  invoice: any
  onCreatePaymentLink: (paymentMethod: 'card' | 'ach' | 'both') => void
  loading: boolean
  achEnabled: boolean
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({ 
  invoice, 
  onCreatePaymentLink,
  loading,
  achEnabled
}) => {
  const [selectedMethod, setSelectedMethod] = useState<'card' | 'ach' | 'both'>('card')
  const isRecurring = invoice?.isRecurring

  // If only card is available, skip the selector and go straight to payment
  if (!achEnabled) {
    return (
      <Card title={isRecurring ? "Set Up Recurring Payment" : "Complete Payment"} style={{ marginBottom: '24px' }}>
        <div style={{ textAlign: 'center', padding: '32px 16px' }}>
          {isRecurring && (
            <Alert
              message="Recurring Payment"
              description={`This is a recurring invoice billed ${invoice.recurringPeriod || 'monthly'}. By continuing, you'll set up automatic payments that will be charged to your payment method each billing cycle.`}
              type="info"
              showIcon
              style={{ marginBottom: '24px', textAlign: 'left' }}
            />
          )}
          
          <div style={{ marginBottom: '24px' }}>
            <Text style={{ fontSize: '16px' }}>
              {isRecurring 
                ? <>Set up automatic payments of <strong>${Number(invoice.totalAmount || 0).toFixed(2)}/{invoice.recurringPeriod || 'month'}</strong></>
                : <>Ready to pay <strong>${Number(invoice.totalAmount || 0).toFixed(2)}</strong>?</>
              }
            </Text>
          </div>
          
          <div style={{ marginBottom: '24px' }}>
            <Button
              type="primary"
              size="large"
              icon={<CreditCardOutlined />}
              onClick={() => onCreatePaymentLink('card')}
              loading={loading}
              style={{
                height: '48px',
                fontSize: '16px',
                padding: '0 32px'
              }}
            >
              {isRecurring ? 'Set Up Auto-Pay with Card' : 'Pay with Card'}
            </Button>
          </div>
          
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              You will be redirected to Stripe's secure payment page.<br />
              Your payment is secured by Stripe.
            </Text>
          </div>
        </div>
      </Card>
    )
  }

  return (
    <Card title={isRecurring ? "Set Up Recurring Payment" : "Select Payment Method"} style={{ marginBottom: '24px' }}>
      <div style={{ padding: '16px 0' }}>
        {isRecurring && (
          <Alert
            message="Recurring Payment"
            description={`This is a recurring invoice billed ${invoice.recurringPeriod || 'monthly'}. By continuing, you'll set up automatic payments that will be charged to your selected payment method each billing cycle.`}
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}
        
        <div style={{ marginBottom: '24px' }}>
          <Text style={{ fontSize: '16px' }}>
            {isRecurring 
              ? <>Set up automatic payments of <strong>${Number(invoice.totalAmount || 0).toFixed(2)}/{invoice.recurringPeriod || 'month'}</strong></>
              : <>How would you like to pay <strong>${Number(invoice.totalAmount || 0).toFixed(2)}</strong>?</>
            }
          </Text>
        </div>

        <Radio.Group
          value={selectedMethod}
          onChange={(e) => setSelectedMethod(e.target.value)}
          style={{ width: '100%', marginBottom: '24px' }}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio 
              value="card" 
              style={{ 
                padding: '16px', 
                border: '1px solid #d9d9d9', 
                borderRadius: '8px', 
                width: '100%',
                background: selectedMethod === 'card' ? '#f0f5ff' : 'transparent'
              }}
            >
              <Space>
                <CreditCardOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Pay with Card</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Credit or debit card • Instant payment
                  </div>
                </div>
              </Space>
            </Radio>
            
            <Radio 
              value="ach" 
              style={{ 
                padding: '16px', 
                border: '1px solid #d9d9d9', 
                borderRadius: '8px', 
                width: '100%',
                background: selectedMethod === 'ach' ? '#f6ffed' : 'transparent'
              }}
            >
              <Space>
                <BankOutlined style={{ fontSize: '20px', color: '#52c41a' }} />
                <div>
                  <div style={{ fontWeight: 500 }}>Pay with Bank Account (ACH)</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    Direct bank transfer • 2-4 business days • Lower fees
                  </div>
                </div>
              </Space>
            </Radio>
          </Space>
        </Radio.Group>

        {selectedMethod === 'ach' && (
          <Alert
            message={isRecurring ? "About ACH Recurring Payments" : "About ACH Bank Transfers"}
            description={isRecurring 
              ? "Your bank account will be charged automatically each billing cycle. ACH payments take 2-4 business days to process. You'll securely connect your bank account through Stripe."
              : "ACH payments are debited directly from your bank account. The payment takes 2-4 business days to process. You'll securely connect your bank account through Stripe."
            }
            type="info"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}
        
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            icon={selectedMethod === 'ach' ? <BankOutlined /> : <CreditCardOutlined />}
            onClick={() => onCreatePaymentLink(selectedMethod)}
            loading={loading}
            style={{
              height: '48px',
              fontSize: '16px',
              padding: '0 32px',
              background: selectedMethod === 'ach' ? '#52c41a' : undefined,
              borderColor: selectedMethod === 'ach' ? '#52c41a' : undefined
            }}
          >
            {isRecurring 
              ? (selectedMethod === 'ach' ? 'Set Up Auto-Pay with Bank' : 'Set Up Auto-Pay with Card')
              : (selectedMethod === 'ach' ? 'Continue with Bank Transfer' : 'Continue with Card')
            }
          </Button>
        </div>
        
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            You will be redirected to Stripe's secure payment page.<br />
            Your payment information is secured by Stripe.
          </Text>
        </div>
      </div>
    </Card>
  )
}

interface PaymentIframeProps {
  paymentLinkUrl: string
  invoice: any
  paymentMethod?: 'card' | 'ach' | 'both'
}

const PaymentRedirect: React.FC<PaymentIframeProps> = ({ paymentLinkUrl, invoice, paymentMethod = 'card' }) => {
  const handlePayNow = () => {
    window.open(paymentLinkUrl, '_blank')
  }

  const isACH = paymentMethod === 'ach'
  const isRecurring = invoice?.isRecurring

  return (
    <Card title={isRecurring ? "Complete Subscription Setup" : "Complete Payment"} style={{ marginBottom: '24px' }}>
      <div style={{ textAlign: 'center', padding: '32px 16px' }}>
        <div style={{ marginBottom: '24px' }}>
          <Text style={{ fontSize: '16px' }}>
            {isRecurring 
              ? <>Set up automatic payments of <strong>${Number(invoice.totalAmount || 0).toFixed(2)}/{invoice.recurringPeriod || 'month'}</strong> for Invoice {invoice.invoiceNumber}</>
              : <>Ready to pay <strong>${Number(invoice.totalAmount || 0).toFixed(2)}</strong> for Invoice {invoice.invoiceNumber}?</>
            }
          </Text>
        </div>

        {isRecurring && (
          <Alert
            message="Recurring Payment"
            description={`By continuing, you'll set up automatic payments that will be charged to your ${isACH ? 'bank account' : 'card'} each billing cycle.`}
            type="info"
            showIcon
            style={{ marginBottom: '24px', textAlign: 'left' }}
          />
        )}

        {isACH && !isRecurring && (
          <Alert
            message="Bank Transfer Payment"
            description="You'll be redirected to connect your bank account. ACH payments typically take 2-4 business days to process."
            type="info"
            showIcon
            style={{ marginBottom: '24px', textAlign: 'left' }}
          />
        )}
        
        <div style={{ marginBottom: '24px' }}>
          <Button
            type="primary"
            size="large"
            icon={isACH ? <BankOutlined /> : <CreditCardOutlined />}
            onClick={handlePayNow}
            style={{
              height: '48px',
              fontSize: '16px',
              padding: '0 32px',
              background: isACH ? '#52c41a' : undefined,
              borderColor: isACH ? '#52c41a' : undefined
            }}
          >
            {isRecurring 
              ? (isACH ? 'Set Up Auto-Pay with Bank' : 'Set Up Auto-Pay with Card')
              : (isACH ? 'Pay with Bank Account' : 'Pay Now with Card')
            }
          </Button>
        </div>
        
        <div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            You will be redirected to Stripe's secure payment page.<br />
            Your payment information is secured by Stripe.
          </Text>
        </div>
      </div>
    </Card>
  )
}

export const InvoicePayment: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>()
  const navigate = useNavigate()
  const { portalConfig } = useCustomerAuth()
  const [invoice, setInvoice] = useState<any>(null)
  const [paymentLinkUrl, setPaymentLinkUrl] = useState<string | null>(null)
  const [helcimCheckout, setHelcimCheckout] = useState<{
    checkoutToken: string
    secretToken: string
    amount: number
  } | null>(null)
  const [banccardCheckout, setBanccardCheckout] = useState<{
    checkoutToken: string
    secretToken: string
    tokenizationKey: string
    amount: number
  } | null>(null)
  const [finixCheckout, setFinixCheckout] = useState<{
    checkoutToken: string
    secretToken: string
    amount: number
    finix: {
      applicationId: string | null
      merchantId: string
      merchantIdentityId: string | null
      environment: 'sandbox' | 'live'
      amount: number
      currency: string
      applePayEnabled?: boolean
      googlePayEnabled?: boolean
      achEnabled?: boolean
      paymentMethods?: Array<'card' | 'bank'>
    }
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [creatingLink, setCreatingLink] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'card' | 'ach' | 'both'>('card')
  const [achEnabled, setAchEnabled] = useState(false)
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<Array<{ id: string; type: string; last4?: string; bankName?: string }>>([])
  const [payWithPlaidLoading, setPayWithPlaidLoading] = useState(false)
  const [selectedPlaidBankId, setSelectedPlaidBankId] = useState<string | null>(null)

  useEffect(() => {
    if (invoiceId) {
      loadInvoiceAndPaymentMethods()
    }
  }, [invoiceId])

  const loadInvoiceAndPaymentMethods = async () => {
    try {
      setLoading(true)
      setError(null)

      const [invoiceData, paymentMethodsData, methodsRes] = await Promise.all([
        customerApi.getInvoice(invoiceId!),
        customerApi.getAvailablePaymentMethods().catch(() => ({ achEnabled: false })),
        customerApi.getMyPaymentMethods().catch(() => ({ data: [] }))
      ])

      setInvoice(invoiceData)
      setAchEnabled(paymentMethodsData.achEnabled || false)
      setSavedPaymentMethods(methodsRes.data || [])
    } catch (err: any) {
      console.error('Failed to load invoice:', err)
      setError(err.response?.data?.message || 'Failed to load invoice information')
    } finally {
      setLoading(false)
    }
  }

  const loadInvoice = async () => {
    await loadInvoiceAndPaymentMethods()
  }

  const handleCreatePaymentLink = async (paymentMethod: 'card' | 'ach' | 'both') => {
    setSelectedPaymentMethod(paymentMethod)
    try {
      setCreatingLink(true)
      setError(null)
      const paymentData = await customerApi.createPaymentIntent(invoiceId!, paymentMethod)
      if (paymentData.processor === 'helcim' && paymentData.checkoutToken && paymentData.secretToken) {
        setHelcimCheckout({
          checkoutToken: paymentData.checkoutToken,
          secretToken: paymentData.secretToken,
          amount: paymentData.amount,
        })
      } else if (
        paymentData.processor === 'banccard' &&
        paymentData.checkoutToken &&
        paymentData.secretToken &&
        paymentData.publishableKey
      ) {
        setBanccardCheckout({
          checkoutToken: paymentData.checkoutToken,
          secretToken: paymentData.secretToken,
          tokenizationKey: paymentData.publishableKey,
          amount: paymentData.amount,
        })
      } else if (
        paymentData.processor === 'finix' &&
        paymentData.checkoutToken &&
        paymentData.secretToken &&
        paymentData.finix
      ) {
        setFinixCheckout({
          checkoutToken: paymentData.checkoutToken,
          secretToken: paymentData.secretToken,
          amount: paymentData.amount,
          finix: paymentData.finix,
        })
      } else if (paymentData.paymentLinkUrl) {
        setPaymentLinkUrl(paymentData.paymentLinkUrl)
      } else {
        throw new Error('No payment checkout was returned')
      }
    } catch (err: any) {
      console.error('Failed to create payment link:', err)
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to create payment link'
      )
    } finally {
      setCreatingLink(false)
    }
  }

  const handlePayWithPlaid = async () => {
    if (!selectedPlaidBankId || !invoiceId) return
    setPayWithPlaidLoading(true)
    setError(null)
    try {
      const result = await customerApi.payInvoiceWithPlaid(invoiceId, selectedPlaidBankId)
      if (result.success) {
        message.success(result.message || 'Payment initiated. It may take a few business days to complete.')
        setPaymentSuccess(true)
      } else {
        setError(result.error || 'Payment failed')
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Payment failed')
    } finally {
      setPayWithPlaidLoading(false)
    }
  }

  const handlePaymentSuccess = () => {
    setPaymentSuccess(true)
  }

  const handlePaymentError = (errorMessage: string) => {
    setError(errorMessage)
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>Loading payment information...</p>
      </div>
    )
  }

  if (error) {
    return (
      <Alert
        message="Payment Error"
        description={error}
        type="error"
        showIcon
        action={
          <Space>
            <Button size="small" onClick={() => navigate('/invoices')}>
              Back to Invoices
            </Button>
            <Button size="small" type="primary" onClick={loadInvoice}>
              Retry
            </Button>
          </Space>
        }
      />
    )
  }

  if (paymentSuccess) {
    return (
      <Result
        icon={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
        title="Payment Successful!"
        subTitle={`Your payment for Invoice ${invoice?.invoiceNumber} has been processed successfully.`}
        extra={[
          <Button type="primary" key="invoices" onClick={() => navigate('/invoices')}>
            View All Invoices
          </Button>,
          <Button key="dashboard" onClick={() => navigate(portalConfig?.defaultLandingPage || '/dashboard')}>
            Back to Dashboard
          </Button>
        ]}
      />
    )
  }

  if (!invoice) {
    return (
      <Alert
        message="Invoice Not Found"
        description="The requested invoice could not be found or is not available for payment."
        type="warning"
        showIcon
        action={
          <Button onClick={() => navigate('/invoices')}>
            Back to Invoices
          </Button>
        }
      />
    )
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto', padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/invoices')}
          style={{ marginBottom: '16px' }}
        >
          Back to Invoices
        </Button>
        <Title level={2}>Pay Invoice</Title>
      </div>

      <Card title="Invoice Details" style={{ marginBottom: '24px' }}>
        <Descriptions column={1}>
          <Descriptions.Item label="Invoice Number">
            <Text strong>{invoice.invoiceNumber}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Amount Due">
            <Statistic
              value={Number(invoice.totalAmount || 0)}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#1890ff', fontSize: '24px' }}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Due Date">
            {invoice.dueAt ? dayjs(invoice.dueAt).format('MMMM D, YYYY') : 'No due date'}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Text type={invoice.status === 'PAID' ? 'success' : 'warning'}>
              {invoice.status}
            </Text>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {invoice.status !== 'PAID' && !paymentLinkUrl && !helcimCheckout && !finixCheckout && !banccardCheckout && (
        <AffirmFinancingBlock
          amountCents={amountDollarsToCents(Number(invoice.balanceAmount ?? invoice.totalAmount ?? 0))}
          invoiceId={invoice.id}
          source="invoice_payment"
        />
      )}

      {invoice.status !== 'PAID' && !paymentLinkUrl && !helcimCheckout && !finixCheckout && !banccardCheckout && (
        <PaymentMethodSelector
          invoice={invoice}
          onCreatePaymentLink={handleCreatePaymentLink}
          loading={creatingLink}
          achEnabled={achEnabled && invoice?.invoiceType !== 'deposit'}
        />
      )}

      {invoice.status !== 'PAID' &&
        selectedPaymentMethod === 'ach' &&
        achEnabled &&
        !paymentLinkUrl &&
        !helcimCheckout &&
        !finixCheckout &&
        !banccardCheckout && (
        <Card title="Pay with Bank Account" style={{ marginBottom: '24px' }}>
          {savedPaymentMethods.filter((m) => m.type === 'plaid_bank' || m.type === 'us_bank_account').length === 0 ? (
            <Alert
              message="No bank account on file"
              description="Add a bank account in Subscriptions to pay this invoice with your bank."
              type="info"
              showIcon
              action={
                <Button size="small" onClick={() => navigate('/subscriptions')}>
                  Go to Subscriptions
                </Button>
              }
            />
          ) : (
            <>
              <Radio.Group
                value={selectedPlaidBankId || undefined}
                onChange={(e) => setSelectedPlaidBankId(e.target.value)}
                style={{ width: '100%', marginBottom: 16 }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  {savedPaymentMethods
                    .filter((m) => m.type === 'plaid_bank' || m.type === 'us_bank_account')
                    .map((m) => (
                      <Radio key={m.id} value={m.id}>
                        <BankOutlined /> {m.bankName || 'Bank'} •••• {m.last4 || '****'}
                      </Radio>
                    ))}
                </Space>
              </Radio.Group>
              <Button
                type="primary"
                size="large"
                icon={<BankOutlined />}
                onClick={handlePayWithPlaid}
                loading={payWithPlaidLoading}
                disabled={!selectedPlaidBankId}
              >
                Pay {Number(invoice?.balanceAmount ?? invoice?.totalAmount ?? 0).toFixed(2)} with selected bank
              </Button>
            </>
          )}
        </Card>
      )}

      {invoice.status !== 'PAID' && finixCheckout && invoiceId && (
        <Card
          title={
            finixCheckout.finix.paymentMethods?.includes('bank') &&
            !finixCheckout.finix.paymentMethods?.includes('card')
              ? 'Secure Bank Payment'
              : 'Secure Card Payment'
          }
          style={{ marginBottom: '24px' }}
        >
          <FinixPayCheckout
            checkoutToken={finixCheckout.checkoutToken}
            secretToken={finixCheckout.secretToken}
            finix={finixCheckout.finix}
            amountLabel={`Pay $${finixCheckout.amount.toFixed(2)} for Invoice ${invoice.invoiceNumber}`}
            apiBaseUrl={env.apiBaseUrl.replace(/\/api$/, '')}
            pdfToken=""
            onValidate={async (payload) => {
              await customerApi.validateHelcimInvoicePayment(invoiceId, payload)
            }}
            onSuccess={() => setPaymentSuccess(true)}
            onError={(msg) => setError(msg)}
          />
        </Card>
      )}

      {invoice.status !== 'PAID' && banccardCheckout && invoiceId && (
        <Card title="Secure Card Payment" style={{ marginBottom: '24px' }}>
          <BanccardPayCheckout
            checkoutToken={banccardCheckout.checkoutToken}
            secretToken={banccardCheckout.secretToken}
            tokenizationKey={banccardCheckout.tokenizationKey}
            amountLabel={`Pay $${banccardCheckout.amount.toFixed(2)} for Invoice ${invoice.invoiceNumber}`}
            preferredPayment={selectedPaymentMethod === 'ach' ? 'check' : 'creditcard'}
            allowAch={achEnabled && selectedPaymentMethod === 'ach'}
            onValidate={async (payload) => {
              await customerApi.validateHelcimInvoicePayment(invoiceId, payload)
            }}
            onSuccess={() => setPaymentSuccess(true)}
            onError={(msg) => setError(msg)}
          />
        </Card>
      )}

      {invoice.status !== 'PAID' && helcimCheckout && invoiceId && (
        <Card title="Secure Card Payment" style={{ marginBottom: '24px' }}>
          <HelcimPayCheckout
            checkoutToken={helcimCheckout.checkoutToken}
            secretToken={helcimCheckout.secretToken}
            amountLabel={`Pay $${helcimCheckout.amount.toFixed(2)} for Invoice ${invoice.invoiceNumber}`}
            onValidate={async (payload) => {
              await customerApi.validateHelcimInvoicePayment(invoiceId, payload)
            }}
            onSuccess={() => setPaymentSuccess(true)}
            onError={(msg) => setError(msg)}
          />
        </Card>
      )}

      {invoice.status !== 'PAID' && paymentLinkUrl && (
        <PaymentRedirect
          paymentLinkUrl={paymentLinkUrl}
          invoice={invoice}
          paymentMethod={selectedPaymentMethod}
        />
      )}

      {invoice.status === 'PAID' && (
        <Alert
          message="Invoice Already Paid"
          description="This invoice has already been paid."
          type="success"
          showIcon
        />
      )}
    </div>
  )
}
